import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles, type Article } from "@/mocks/articleData";

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const article: Article | undefined = useMemo(() => {
    return articles.find((a) => a.id === Number(id));
  }, [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="pt-16">
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">
            文章未找到
          </h2>
          <p className="text-sm text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
          <Link
            to="/category/platform"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            {t("article.back")}
          </Link>
        </div>
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      "@type": "Organization",
      name: "必赢国际",
      url: "https://www.biyingguoji.com",
    },
    url: `https://www.biyingguoji.com/article/${article.id}`,
  };

  return (
    <div className="pt-16">
      <SEOHead
        title={`${article.title} - 必赢国际`}
        description={article.summary}
        keywords={`必赢国际,${article.categoryName},${article.title}`}
        canonicalUrl={`https://www.biyingguoji.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogType="article"
        structuredData={structuredData}
      />

      <div className="bg-background-100 border-b border-background-200/70">
        <Breadcrumb
          items={[
            { label: article.categoryName, to: `/category/${article.category}` },
            { label: article.title },
          ]}
        />
      </div>

      <article className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          {/* Article Header */}
          <header className="mb-10">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors mb-4"
            >
              <i className="ri-arrow-left-line"></i>
              {article.categoryName}
            </Link>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </header>

          {/* Article Content */}
          <div className="prose-custom">
            {article.content.map((paragraph, index) => (
              <p
                key={index}
                className="text-base text-foreground-700 leading-relaxed mb-5"
              >
                {paragraph}
              </p>
            ))}
          </div>

          {/* Divider */}
          <hr className="my-12 border-background-200/70" />

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                {t("article.related")}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{related.date}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Back Button */}
          <div className="mt-10">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary-100 text-secondary-700 rounded-lg text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              {t("article.back")}
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}